/**
 * Analytics Selectors
 * NgRx selectors for analytics state
 */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AnalyticsState } from '../models/analytics.models';

export const selectAnalyticsState = createFeatureSelector<AnalyticsState>('analytics');

// Basic Selectors
export const selectOverview = createSelector(
  selectAnalyticsState,
  (state) => state.overview
);

export const selectDocumentAnalytics = createSelector(
  selectAnalyticsState,
  (state) => state.documentAnalytics
);

export const selectApiUsageAnalytics = createSelector(
  selectAnalyticsState,
  (state) => state.apiUsageAnalytics
);

export const selectCostAnalytics = createSelector(
  selectAnalyticsState,
  (state) => state.costAnalytics
);

export const selectTemplateAnalytics = createSelector(
  selectAnalyticsState,
  (state) => state.templateAnalytics
);

export const selectProviderAnalytics = createSelector(
  selectAnalyticsState,
  (state) => state.providerAnalytics
);

export const selectDateRange = createSelector(
  selectAnalyticsState,
  (state) => state.dateRange
);

export const selectGroupBy = createSelector(
  selectAnalyticsState,
  (state) => state.groupBy
);

export const selectLoading = createSelector(
  selectAnalyticsState,
  (state) => state.loading
);

export const selectError = createSelector(
  selectAnalyticsState,
  (state) => state.error
);

// Computed Selectors
export const selectHasData = createSelector(
  selectOverview,
  (overview) =>
    !!overview &&
    ((overview.total_documents ?? 0) > 0 || (overview.total_api_calls ?? 0) > 0)
);

export const selectDocumentSuccessRate = createSelector(
  selectDocumentAnalytics,
  (analytics) => {
    if (!analytics || analytics.total_documents === 0) return 0;
    return (analytics.successful_documents / analytics.total_documents) * 100;
  }
);

export const selectApiSuccessRate = createSelector(
  selectApiUsageAnalytics,
  (analytics) => {
    if (!analytics || analytics.total_requests === 0) return 0;
    return (analytics.successful_requests / analytics.total_requests) * 100;
  }
);

export const selectTotalCost = createSelector(
  selectCostAnalytics,
  (analytics) => analytics?.total_cost || 0
);

export const selectAverageDailyCost = createSelector(
  selectCostAnalytics,
  (analytics) => {
    if (!analytics || !analytics.metrics || analytics.metrics.length === 0) return 0;
    const totalCost = analytics.metrics.reduce((sum, m) => sum + m.total_cost, 0);
    return totalCost / analytics.metrics.length;
  }
);

export const selectCostByProvider = createSelector(
  selectCostAnalytics,
  (analytics) => {
    if (!analytics || !analytics.metrics) return null;
    
    const totals = analytics.metrics.reduce((acc, metric) => ({
      openai: acc.openai + metric.openai_cost,
      claude: acc.claude + metric.claude_cost,
      gemini: acc.gemini + metric.gemini_cost
    }), { openai: 0, claude: 0, gemini: 0 });
    
    return totals;
  }
);

export const selectHasProviderCosts = createSelector(
  selectCostByProvider,
  (costs) => !!costs && (costs.openai > 0 || costs.claude > 0 || costs.gemini > 0)
);

export const selectAverageConfidence = createSelector(
  selectDocumentAnalytics,
  (analytics) => {
    if (!analytics?.metrics?.length) return 0;
    const total = analytics.metrics.reduce((sum, metric) => sum + (metric.average_confidence || 0), 0);
    return total / analytics.metrics.length;
  }
);

export const selectAverageLatency = createSelector(
  selectDocumentAnalytics,
  (analytics) => {
    if (!analytics?.metrics?.length) return 0;
    const total = analytics.metrics.reduce((sum, metric) => sum + (metric.average_processing_time || 0), 0);
    return total / analytics.metrics.length;
  }
);

export const selectSuccessRateTrend = createSelector(
  selectDocumentAnalytics,
  (analytics) => periodComparisonTrend(
    analytics?.metrics,
    (metric) => metric.total_documents > 0
      ? (metric.successful_documents / metric.total_documents) * 100
      : 0
  )
);

export const selectConfidenceTrend = createSelector(
  selectDocumentAnalytics,
  (analytics) => periodComparisonTrend(
    analytics?.metrics,
    (metric) => metric.average_confidence || 0
  )
);

export const selectLatencyTrend = createSelector(
  selectDocumentAnalytics,
  (analytics) => periodComparisonTrend(
    analytics?.metrics,
    (metric) => metric.average_processing_time || 0
  )
);

export const selectErrorRateTrend = createSelector(
  selectDocumentAnalytics,
  (analytics) => periodComparisonTrend(
    analytics?.metrics,
    (metric) => metric.total_documents > 0
      ? (metric.failed_documents / metric.total_documents) * 100
      : 0
  )
);

function periodComparisonTrend<T extends { total_documents?: number }>(
  metrics: T[] | undefined,
  getValue: (metric: T) => number
): { change: number; isIncreasing: boolean; isDecreasing: boolean } | null {
  if (!metrics || metrics.length < 2) return null;

  const midpoint = Math.floor(metrics.length / 2);
  const previous = metrics.slice(0, midpoint);
  const recent = metrics.slice(midpoint);
  if (!previous.length || !recent.length) return null;

  const average = (items: T[]) =>
    items.reduce((sum, metric) => sum + getValue(metric), 0) / items.length;

  const previousValue = average(previous);
  const recentValue = average(recent);

  if (previousValue === 0) {
    if (recentValue === 0) {
      return { change: 0, isIncreasing: false, isDecreasing: false };
    }
    return null;
  }

  const change = ((recentValue - previousValue) / previousValue) * 100;
  return {
    change,
    isIncreasing: change > 0,
    isDecreasing: change < 0,
  };
}

export const selectDocumentTrend = createSelector(
  selectDocumentAnalytics,
  (analytics) => {
    if (!analytics || !analytics.metrics || analytics.metrics.length < 2) return null;
    
    const metrics = analytics.metrics;
    const latest = metrics[metrics.length - 1];
    const previous = metrics[metrics.length - 2];
    
    if (previous.total_documents === 0) return null;
    
    const change = ((latest.total_documents - previous.total_documents) / previous.total_documents) * 100;
    return {
      change,
      isIncreasing: change > 0,
      isDecreasing: change < 0
    };
  }
);

export const selectApiUsageTrend = createSelector(
  selectApiUsageAnalytics,
  (analytics) => {
    if (!analytics || !analytics.metrics || analytics.metrics.length < 2) return null;
    
    const metrics = analytics.metrics;
    const latest = metrics[metrics.length - 1];
    const previous = metrics[metrics.length - 2];
    
    if (previous.total_requests === 0) return null;
    
    const change = ((latest.total_requests - previous.total_requests) / previous.total_requests) * 100;
    return {
      change,
      isIncreasing: change > 0,
      isDecreasing: change < 0
    };
  }
);

export const selectDateRangeLabel = createSelector(
  selectDateRange,
  (dateRange) => {
    if (!dateRange.start_date || !dateRange.end_date) return 'All Time';
    
    const start = new Date(dateRange.start_date);
    const end = new Date(dateRange.end_date);
    
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  }
);

